import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../../../shared/Reveal/Reveal';
import { useMarketAnalysisPosts } from '../../../../hooks/useMarketAnalysisPosts';
import { useMarketAnalysisPostsSettingsContent } from '../../../../hooks/useMarketAnalysisPostsSettingsContent';
import './Posts.css';

const POSTS_PER_PAGE = 6;

const ArrowUpRight = () => (
  <svg
    className="market-analysis-posts__arrow"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7 17L17 7M17 7H9M17 7V15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon = () => (
  <svg
    className="market-analysis-posts__search-icon"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M20 20L16.5 16.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const Posts = () => {
  const { posts, isLoading: postsLoading } = useMarketAnalysisPosts();
  const { content: settings, isLoading: settingsLoading } = useMarketAnalysisPostsSettingsContent();
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const categories = settings?.categories || ['All'];
  const listPosts = useMemo(
    () => posts.filter((post) => !post.featured),
    [posts]
  );

  const filteredPosts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return listPosts.filter((post) => {
      const matchesCategory =
        activeCategory === 'All' || post.category === activeCategory;
      const matchesSearch =
        !query ||
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.category.toLowerCase().includes(query) ||
        post.author.toLowerCase().includes(query);

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, listPosts, search]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const visiblePosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  if (postsLoading || settingsLoading) {
    return null;
  }

  const handleCategory = (category) => {
    setActiveCategory(category);
    setPage(1);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const goToPage = (nextPage) => {
    setPage(Math.min(Math.max(1, nextPage), totalPages));
  };

  return (
    <section className="market-analysis-posts">
      <div className="market-analysis-posts__inner">
        <aside className="market-analysis-posts__sidebar">
          <label className="market-analysis-posts__search">
            <SearchIcon />
            <input
              type="search"
              value={search}
              onChange={handleSearch}
              placeholder="Search"
              aria-label="Search market analysis"
            />
          </label>

          <nav className="market-analysis-posts__categories" aria-label="Categories">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={`market-analysis-posts__category${
                  activeCategory === category
                    ? ' market-analysis-posts__category--active'
                    : ''
                }`}
                onClick={() => handleCategory(category)}
              >
                {category}
              </button>
            ))}
          </nav>
        </aside>

        <div className="market-analysis-posts__main">
          <div className="market-analysis-posts__grid">
            {visiblePosts.map((post, index) => {
              const to = `/insights/market-analysis/${post.slug}`;

              return (
                <Reveal key={post.slug} delay={index * 60}>
                  <article className="market-analysis-posts__card">
                    <Link to={to} className="market-analysis-posts__media">
                      <img
                        className="market-analysis-posts__image"
                        src={post.image}
                        alt=""
                      />
                    </Link>

                    <div className="market-analysis-posts__copy">
                      <p className="market-analysis-posts__tag">{post.category}</p>

                      <Link to={to} className="market-analysis-posts__title-link">
                        <h3 className="market-analysis-posts__title">{post.title}</h3>
                        <ArrowUpRight />
                      </Link>

                      <p className="market-analysis-posts__excerpt">{post.excerpt}</p>

                      <div className="market-analysis-posts__author">
                        <img
                          className="market-analysis-posts__avatar"
                          src={post.avatar}
                          alt=""
                          aria-hidden="true"
                        />
                        <div className="market-analysis-posts__author-meta">
                          <p className="market-analysis-posts__author-name">{post.author}</p>
                          <p className="market-analysis-posts__author-date">{post.date}</p>
                        </div>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          {filteredPosts.length === 0 ? (
            <p className="market-analysis-posts__empty">No articles match your search.</p>
          ) : (
            <div className="market-analysis-posts__pagination">
              <button
                type="button"
                className="market-analysis-posts__page-nav"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                ← Previous
              </button>

              <div className="market-analysis-posts__pages">
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((num) => (
                  <button
                    key={num}
                    type="button"
                    className={`market-analysis-posts__page${
                      currentPage === num ? ' market-analysis-posts__page--active' : ''
                    }`}
                    onClick={() => goToPage(num)}
                    aria-current={currentPage === num ? 'page' : undefined}
                  >
                    {num}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="market-analysis-posts__page-nav"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Posts;
